import React from "react";
var __jsx = React.createElement;
import { faChevronDown, faClock, faLink } from "@fortawesome/pro-regular-svg-icons";
import classNames from "classnames";
import { format } from "date-fns";
import { t } from "i18next";
import { get, isEmpty } from "lodash";
import { useRouter } from "next/router";
import AwesomeIcon from "../../../AwesomeIcon";
import DropdownMenu from "../../../common/DropdownMenu";
import Tooltip from "../../../common/Tooltip";
import useGetViewerRoute from "../../../../hooks/useGetViewerRoute";
import { trackEvent } from "../../../../utils/mixpanel";
import { MIXPANEL_EVENTS } from "../../../../utils/mixpanelEvents";
import { formatDateTime } from "../../../../utils/dateTime";
var DICOM_GLYPH = /*#__PURE__*/React.createElement("svg", {
  viewBox: "0 0 10 10",
  fill: "none",
  stroke: "currentColor",
  strokeWidth: "1.4",
  className: "w-2.5 h-2.5 flex-shrink-0"
}, /*#__PURE__*/React.createElement("rect", {
  x: "1",
  y: "2",
  width: "8",
  height: "6",
  rx: "1"
}), /*#__PURE__*/React.createElement("path", {
  d: "M3 5h4M5 3.5v3"
}));

// Active (teal) badge — a study is linked. Opens the DICOM viewer when clickable.
var DicomBadge = function DicomBadge(_ref) {
  var onOpen = _ref.onOpen;
  return /*#__PURE__*/React.createElement("div", {
    className: "flex-shrink-0",
    onClick: function onClick(e) {
      e.stopPropagation();
      onOpen === null || onOpen === void 0 || onOpen();
    }
  }, /*#__PURE__*/React.createElement(Tooltip, {
    tooltipText: onOpen ? "Open DICOM images" : "DICOM images available",
    verticalPosition: "bottom",
    className: "[&_.tooltip-trigger]:m-0 [&_.tooltip-arrow]:hidden [&_.tooltip-content]:text-xs [&_.tooltip-content]:px-2 [&_.tooltip-content]:py-1 [&_.tooltip-content]:rounded-md"
  }, /*#__PURE__*/React.createElement("span", {
    className: classNames("inline-flex items-center gap-1 bg-teal-50 dark:bg-teal-950 border border-teal-600 dark:border-teal-500 rounded px-[6px] py-px text-[9px] font-bold tracking-wide text-teal-600 dark:text-teal-400", onOpen ? "cursor-pointer hover:bg-teal-500/10 dark:hover:bg-teal-900" : "cursor-default")
  }, DICOM_GLYPH, "DICOM")));
};

// Muted (grey) badge — no study linked. Used as the trigger to link one.
var MutedDicomBadge = function MutedDicomBadge(_ref2) {
  var open = _ref2.open;
  return /*#__PURE__*/React.createElement("span", {
    className: classNames("inline-flex items-center gap-1 rounded px-[6px] py-px text-[9px] font-bold tracking-wide border bg-gray-50 dark:bg-gray-800 border-gray-300 dark:border-gray-600 text-gray-400 dark:text-gray-500 cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700", {
      "bg-gray-100 dark:bg-gray-700": open
    })
  }, DICOM_GLYPH, "DICOM", /*#__PURE__*/React.createElement(AwesomeIcon, {
    name: faLink,
    className: "w-2.5 h-2.5 flex-shrink-0"
  }));
};
var ReportStrip = function ReportStrip(_ref3) {
  var data = _ref3.data,
    isExpanded = _ref3.isExpanded,
    onToggle = _ref3.onToggle,
    studies = _ref3.studies,
    onLinkStudy = _ref3.onLinkStudy,
    hideDicom = _ref3.hideDicom;
  var router = useRouter();
  var viewerRoute = useGetViewerRoute();
  var canOpenStudy = data.studyId != null && data.patientUid != null;
  var onOpenStudy = function onOpenStudy() {
    if (!canOpenStudy) return;
    trackEvent(MIXPANEL_EVENTS.RADIOLOGY_REPORT_STUDY_OPENED, {
      value: data.studyId
    });
    router.push("".concat(viewerRoute).concat(data.patientUid, "?study_id=").concat(data.studyId), undefined, {
      shallow: true
    });
  };

  // No study linked: a dropdown of the patient's studies to link to (when a link
  // handler is available, i.e. in the chat message), else fall back to "no study".
  var buildLinkOptions = function buildLinkOptions() {
    return studies !== null && studies !== void 0 && studies.length ? studies.map(function (study) {
      var _study$radiologist_re;
      var modality = get(study, ["series", "0", "modality"], "");
      var linkName = "".concat((study === null || study === void 0 ? void 0 : study.study_description) || modality, " | ").concat(format(new Date(study.created_at), "dd/MM/yyyy"));
      var disabled = !isEmpty(study === null || study === void 0 || (_study$radiologist_re = study.radiologist_report) === null || _study$radiologist_re === void 0 ? void 0 : _study$radiologist_re.report);
      return {
        name: /*#__PURE__*/React.createElement("button", {
          className: "flex w-full p-3 text-gray-800 dark:text-white",
          onClick: function onClick() {
            return onLinkStudy === null || onLinkStudy === void 0 ? void 0 : onLinkStudy(study);
          },
          disabled: disabled
        }, linkName),
        className: classNames({
          "hover:!bg-transparent opacity-50": disabled
        })
      };
    }) : [{
      name: /*#__PURE__*/React.createElement("div", {
        className: "flex w-full p-3 text-gray-800 dark:text-white"
      }, t("no_study_available"))
    }];
  };
  var renderDicom = function renderDicom() {
    if (hideDicom) return null;
    if (canOpenStudy) return /*#__PURE__*/React.createElement(DicomBadge, {
      onOpen: onOpenStudy
    });
    if (data.hasDicom) return /*#__PURE__*/React.createElement(DicomBadge, null);
    // Not attached: a link trigger when we can link, otherwise a static muted badge.
    if (!onLinkStudy) return /*#__PURE__*/React.createElement("div", {
      className: "flex-shrink-0"
    }, /*#__PURE__*/React.createElement(MutedDicomBadge, null));
    return /*#__PURE__*/React.createElement("div", {
      className: "flex-shrink-0",
      onClick: function onClick(e) {
        return e.stopPropagation();
      }
    }, /*#__PURE__*/React.createElement(DropdownMenu, {
      hasCustomControl: true,
      useFixedPositioning: true,
      options: buildLinkOptions(),
      Icon: function Icon(_ref4) {
        var open = _ref4.open;
        return /*#__PURE__*/React.createElement(MutedDicomBadge, {
          open: open
        });
      }
    }));
  };
  return /*#__PURE__*/React.createElement("div", {
    className: "flex items-center justify-between px-[14px] py-[9px] cursor-pointer select-none gap-2 hover:bg-teal-50/40 dark:hover:bg-teal-900/20 transition-colors duration-150 rounded-t-xl",
    onClick: onToggle
  }, /*#__PURE__*/React.createElement("div", {
    className: "flex items-center gap-3 min-w-0 flex-1"
  }, data.modality && /*#__PURE__*/React.createElement("span", {
    className: "text-[11px] font-bold tracking-widest uppercase text-gray-400 dark:text-gray-500 flex-shrink-0"
  }, data.modality), /*#__PURE__*/React.createElement("span", {
    className: "text-[12px] font-semibold uppercase tracking-[0.05em] text-gray-800 dark:text-gray-100 truncate min-w-0",
    title: data.examDescription
  }, data.examDescription), data.examDate && /*#__PURE__*/React.createElement("span", {
    className: "inline-flex items-center gap-2 text-[11px] text-gray-700 dark:text-gray-300 whitespace-nowrap flex-shrink-0"
  }, /*#__PURE__*/React.createElement("span", {
    className: "font-medium uppercase tracking-[0.06em] text-gray-400 dark:text-gray-500"
  }, "Exam Date"), data.examDate)), /*#__PURE__*/React.createElement("div", {
    className: "flex items-center gap-3 flex-shrink-0"
  }, renderDicom(), data.timestamp && /*#__PURE__*/React.createElement("span", {
    className: "inline-flex items-center gap-2 text-[11px] text-gray-500 dark:text-gray-400 whitespace-nowrap"
  }, /*#__PURE__*/React.createElement(AwesomeIcon, {
    name: faClock,
    className: "w-[13px] h-[13px] flex-shrink-0"
  }), formatDateTime(data.timestamp)), /*#__PURE__*/React.createElement(AwesomeIcon, {
    name: faChevronDown,
    className: classNames("w-[13px] h-[13px] text-gray-400 dark:text-gray-500 transition-transform duration-200 ease-in-out flex-shrink-0", {
      "rotate-180": isExpanded
    })
  })));
};
export default ReportStrip;